import { useMutation, useQuery } from '@apollo/client';
import { useI18n } from '@i18n';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
} from '@mui/material';
import { export_jobs_enum } from '@predium/enums';
import { ensureDefined } from '@predium/utils';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import CompletedBatchExportRow from '@/components/notifications/CompletedBatchExportRow';
import { useDownloads } from '@/components/notifications/DownloadsContext';
import OngoingExportRow from '@/components/notifications/OngoingExportRow';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import Scrollbar from '@/components/Scrollbar';
import { useSession } from '@/components/session/useSession';
import { COMPLETED_BATCH_EXPORTS, DELETE_USER_FILE, USER_FILES_LIST } from '@/graphql/Downloads.queries';
import { deleteCache } from '@/utils/cacheHelpers';
import { durationFromNow } from '@/utils/formatTime';
import { getFileIcon } from '@/utils/getFileIcon';

export default function DownloadsTab() {
  const { t, language } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const { user, org } = useSession();

  const { ongoingExports, downloadFile, downloadBatchExport } = useDownloads();

  const { data: userFilesListData } = useQuery(USER_FILES_LIST, {
    variables: { userId: user.id },
    fetchPolicy: 'cache-and-network',
  });

  const { data: completedBatchExportsData } = useQuery(COMPLETED_BATCH_EXPORTS, {
    variables: { userId: user.id, title: export_jobs_enum.BATCH_ESG_ANALYSIS_PDF_EXPORT },
    fetchPolicy: 'cache-and-network',
  });

  const userFiles = userFilesListData?.file ?? [];
  const completedBatchExports = completedBatchExportsData?.async_job ?? [];
  const previousDownloads = [
    ...userFiles.map((file) => ({ kind: 'file' as const, createdAt: file.created_at, file })),
    ...completedBatchExports.map((batchExport) => ({
      kind: 'batch' as const,
      createdAt: batchExport.created_at,
      batchExport,
    })),
  ].sort(
    (left, right) =>
      (right.createdAt ? new Date(right.createdAt).getTime() : 0) -
      (left.createdAt ? new Date(left.createdAt).getTime() : 0),
  );
  const hasPreviousDownloads = userFiles.length > 0 || completedBatchExports.length > 0;

  // Show loading state while files are being loaded
  const isLoading = !userFiles && ongoingExports.length === 0;

  const [deleteFile] = useMutation(DELETE_USER_FILE, {
    onCompleted: () => {
      enqueueSnackbar(t('Downloads_FileDeleted'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    },
    update: (cache, { data }) => {
      if (data?.delete_file_by_pk) {
        deleteCache(cache, data.delete_file_by_pk);
      }
    },
    onError: () => {
      enqueueSnackbar(t('Downloads_FileDeleteError'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
  });

  const handleDelete = useCallback((fileId: number) => deleteFile({ variables: { fileId } }), [deleteFile]);

  return (
    <Scrollbar sx={{ maxHeight: 340 }}>
      {isLoading ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <LinearProgress />
          <PTypography variant="body2" color="secondary" sx={{ mt: 1 }}>
            {t('Downloads_Loading')}
          </PTypography>
        </Box>
      ) : null}

      <Collapse in={ongoingExports.length > 0} timeout={300}>
        <List
          disablePadding
          subheader={
            <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
              {t('Downloads_Processing')}
            </ListSubheader>
          }
        >
          {ongoingExports.map((job) => (
            <OngoingExportRow key={job.id} job={job} orgSector={org.orgSector} />
          ))}
        </List>
      </Collapse>

      {ongoingExports.length > 0 && hasPreviousDownloads ? (
        <Divider sx={{ my: 1 }} data-testid="section-divider" />
      ) : null}

      <Collapse in={hasPreviousDownloads} timeout={300}>
        <List
          disablePadding
          subheader={
            <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
              {t('Downloads_PreviousDownloads')}
            </ListSubheader>
          }
        >
          {previousDownloads.map((download) =>
            download.kind === 'batch' ? (
              <CompletedBatchExportRow
                key={`batch-${download.batchExport.id}`}
                batchExport={download.batchExport}
                onDownload={downloadBatchExport}
              />
            ) : (
              <ListItem key={`file-${download.file.id}`} sx={{ py: 1, px: 2.5, pr: 8 }} data-testid="file-item">
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={getFileIcon(ensureDefined(download.file.name))}
                      alt={`${download.file.name} icon`}
                      style={{
                        width: 24,
                        height: 24,
                        objectFit: 'contain',
                      }}
                      data-testid="file-icon"
                    />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  sx={{ pr: 1 }}
                  primary={
                    <PTypography
                      variant="subtitle2"
                      noOverflow={{ maxWidth: 200, title: download.file.name ?? '' }}
                      sx={{ mb: 0.5, pr: 1 }}
                    >
                      {download.file.name}
                    </PTypography>
                  }
                  secondary={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Iconify icon={ICONS.CLOCK} width={16} height={16} />
                      <PTypography variant="caption" color="secondary">
                        {durationFromNow(download.file.created_at, language)}
                      </PTypography>
                    </Stack>
                  }
                />
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('Downloads_Download')}>
                      <IconButton
                        size="small"
                        onClick={() => downloadFile(download.file.id)}
                        data-testid="download-button"
                      >
                        <Iconify icon={ICONS.EXPORT} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Downloads_Delete')}>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(download.file.id)}
                        data-testid="delete-button"
                      >
                        <Iconify icon={ICONS.TRASH} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
            ),
          )}
        </List>
      </Collapse>

      {ongoingExports.length === 0 && !hasPreviousDownloads && !isLoading ? (
        <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
          {t('Downloads_NoDownloadsAvailable')}
        </ListSubheader>
      ) : null}
    </Scrollbar>
  );
}
